import React, { useEffect, useState } from "react";
import cn from "classnames";
import styles from "./Table.module.sass";
import style from "./Table.module.css";
import Dropdown from "../../../components/Dropdown";
import {
  Table as BootstrapTable,
  Button,
  Form,
  Pagination,
} from "react-bootstrap";
import { cancelOrderStart, getOrdersStart, updateOrderParams, refetchOrders } from "store/crypto/orders/actions";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

const navigationBase = ["Open Orders", "Orders History"];
const subNavigationItems = [
  { label: "Limit | Market", value: "limit|market" },
  { label: "TP/SL", value: "tp|sl" },
  { label: "Trigger Order", value: "trigger" }
];

const Table = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();

  const [navigation, setNavigation] = useState(navigationBase);
  const [activeIndex, setActiveIndex] = useState(0);
  const [subActiveIndex, setSubActiveIndex] = useState(0);

  const [sorting, setSorting] = useState(navigationBase[0]);
  const [date, setDate] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const { orders: orderHistory, pagination } = useSelector((state) => state.crypto.orders);

  useEffect(() => {
    const locationRoutes = location?.pathname?.split("/");
    if (
      locationRoutes?.includes("margin") ||
      locationRoutes?.includes("futures")
    ) {
      setNavigation((prev) => [
        ...new Set([...prev, "Open Positions", "Position History"]),
      ]);
    }
  }, [location]);

  const fetchOrders = () => {
    const params = {
      page,
      limit,
      status: activeIndex === 0 ? "open" : "all",
      type: subNavigationItems[subActiveIndex].value,
      ...(date && { "start-time": date }),
    };
    
    dispatch(updateOrderParams(params));
    
    dispatch(getOrdersStart(params));
  };

  useEffect(() => {
    fetchOrders();
  }, [activeIndex, subActiveIndex, page, limit, date]);

  const handleCancelOrder = (orderId) => {
    if (window.confirm(t("Are you sure you want to cancel this order?"))) {
      dispatch(cancelOrderStart(orderId));
      
      // Refetch orders after a short delay to ensure the cancellation has been processed
      setTimeout(() => {
        dispatch(refetchOrders());
      }, 1000);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    return `${d.toLocaleDateString()}, ${d.toLocaleTimeString()}`;
  };

  // get to date
  const getToDate = (fromDate) => {
    if (!fromDate) return "";
    const d = new Date(fromDate);
    d.setDate(d.getDate() + 1);
    return d.toISOString().split("T")[0];
  };

  return (
    <div className={styles.inner}>

      {/* Top Navigation Tabs */}
      <div className={styles.nav}>
        {navigation.map((label, idx) => (
          <button
            key={idx}
            className={cn("orders-tab", styles.link, {
              [styles.active]: idx === activeIndex,
            })}
            onClick={() => {
              setActiveIndex(idx);
              setSubActiveIndex(0);
              setPage(1);
            }}
          >
            {t(label)}
          </button>
        ))}
      </div>

      {/* Sub Navigation Tabs */}
      <div className={styles.nav}>
        {subNavigationItems.map((item, idx) => (
          <button
            key={idx}
            className={cn(style.subNav, styles.link, {
              [styles.active]: idx === subActiveIndex,
            })}
            onClick={() => {
              setSubActiveIndex(idx);
              setPage(1);
            }}
          >
            {t(item.label)}
          </button>
        ))}
      </div>

      {/* Table */}
      <BootstrapTable striped bordered hover responsive className="mt-3">
        {/* Table Header */}
        <thead>
          <tr>
            <th>{t("Time")}</th>
            <th>{t("Pair")}</th>
            <th>{t("Type")}</th>
            <th>{t("Side")}</th>
            <th>{t(activeIndex === 0 ? "Price" : "Average Price")}</th>
            <th>{t("Amount")}</th>
            <th>{t("Total")}</th>
            {["TP/SL", "Trigger Order"].includes(
              subNavigationItems[subActiveIndex].value
            ) && <th>{t("Trigger condition")}</th>}
            {activeIndex !== 0 && <th>{t("Status")}</th>}
            {activeIndex === 0 && <th>{t("Actions")}</th>}
          </tr>
        </thead>
        {/* Table Body */}
        <tbody>
          {orderHistory?.length ? (
            orderHistory.map((x, idx) => {
              const price = x?.price?.$numberDecimal ?? x?.price;
              const amount = x?.amount?.$numberDecimal ?? x?.amount;
              const total = price && amount ? price * amount : "-";

              return (
                <tr key={idx}>
                  <td>{formatDate(x?.createdAt)}</td>
                  <td>{x?.symbol}</td>
                  <td>{x?.type?.toUpperCase()}</td>
                  <td>{x?.side?.toUpperCase()}</td>
                  <td>{isNaN(price)?"-":price}</td>
                  <td>{isNaN(amount)?"-":amount}</td>
                  <td>{isNaN(total)?"-":total}</td>
                  {["TP/SL", "Trigger Order"].includes(
                    subNavigationItems[subActiveIndex].value
                  ) && <td>{x?.triggerCondition}</td>}
                  {activeIndex !== 0 && <td>{x?.status}</td>}
                  {activeIndex === 0 && (
                    <td>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleCancelOrder(x?.id)}
                        disabled={x?.status === "cancelled"}
                      >
                        {t("Cancel")}
                      </Button>
                    </td>
                  )}
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan="10" className="text-center text-muted py-4">
                {t("No orders found.")}
              </td>
            </tr>
          )}
        </tbody>
      </BootstrapTable>

      <div className="d-flex justify-content-end align-items-center">
        <Pagination
          className={`justify-content-center mt-3 ${style.customPagination}`}
        >
          <Pagination.Prev
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          />
          <Pagination.Item active>{page}</Pagination.Item>
          <Pagination.Next
            disabled={page === pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          />
        </Pagination>
      </div>
    </div>
  );
};

export default Table;
